import { useState, useEffect, useRef, useMemo } from 'react';
import { PrismTable, PrismInputText, PrismInputTextarea, PrismButton, PrismDialog, PrismSelect, PrismCheckbox, PrismBanner, PrismLoading } from '@prism-ui/react';
import { onCheckedChange } from '../../../../utils/prismCheckbox';
import axios from 'axios';
import { toast } from '../../../Toaster/toast';
import './Tags.css';
import AdminPane from '../../AdminPane';

type Tag = {
  id: number;
  name: string;
  description?: string | null;
};

const ProjectTags = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [newTag, setNewTag] = useState('');
  const [newTagDescription, setNewTagDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortAsc, setSortAsc] = useState(true);

  // Edit dialog (opened from a row's "Modify" action). Holds the working name +
  // definition so edits are staged locally and saved explicitly — no inline row editing.
  const [editName, setEditingName] = useState<string>('');
  const [editDescription, setEditingDescription] = useState<string>('');
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);
  const [tagToDeleteProjectCount, setTagToDeleteProjectCount] = useState<number>(0);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showFinalDeleteConfirmModal, setShowFinalDeleteConfirmModal] = useState(false);
  const [finalDeleteModalError, setFinalDeleteModalError] = useState('');
  
  // Migrate modal state
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [selectedMigrateToTag, setSelectedMigrateToTag] = useState<number | null>(null);
  const [createNewMigrateTag, setCreateNewMigrateTag] = useState(false);
  const [newMigrateTagName, setNewMigrateTagName] = useState('');
  const [showMigrateReviewModal, setShowMigrateReviewModal] = useState(false);
  const [showAffectedProjects, setShowAffectedProjects] = useState(false);
  const [affectedProjects, setAffectedProjects] = useState<Array<{id: number; name: string}>>([]);
  const [showFinalDeleteAffectedProjects, setShowFinalDeleteAffectedProjects] = useState(false);
  const [finalDeleteAffectedProjects, setFinalDeleteAffectedProjects] = useState<Array<{id: number; name: string}>>([]);
  
  // Add projects modal state
  const [showAddProjectsModal, setShowAddProjectsModal] = useState(false);
  const [availableProjects, setAvailableProjects] = useState<Array<{id: number; name: string}>>([]);
  const [selectedProjectsToAdd, setSelectedProjectsToAdd] = useState<number[]>([]);
  const [loadingAvailableProjects, setLoadingAvailableProjects] = useState(false);
  const [tagToAddProjects, setTagToAddProjects] = useState<Tag | null>(null);
  const [projectSearchFilter, setProjectSearchFilter] = useState('');
  const [showAddProjectsReviewModal, setShowAddProjectsReviewModal] = useState(false);
  const [showAddProjectsAffectedProjects, setShowAddProjectsAffectedProjects] = useState(false);
  
  // Rename confirmation modal state
  const [showRenameConfirmModal, setShowRenameConfirmModal] = useState(false);
  const [tagToRename, setTagToRename] = useState<{ id: number; oldName: string; newName: string } | null>(null);
  const [tagToRenameProjectCount, setTagToRenameProjectCount] = useState<number>(0);
  const [showRenameAffectedProjects, setShowRenameAffectedProjects] = useState(false);
  const [renameAffectedProjects, setRenameAffectedProjects] = useState<{ id: number; name: string }[]>([]);
  
  // Filter state
  const [filterText, setFilterText] = useState('');
  
  const isMountedRef = useRef(true);
  // Latest "open edit dialog" handler, read by the imperative PrismTable action cell so
  // its click always calls the current closure (the cell node is built outside React).
  const openEditRef = useRef<(tagId: number) => void>(() => {});
  // Set when we programmatically hide the edit dialog to reveal a sub-modal (Migrate /
  // Add Projects / Final Delete). The dialog's `prismClose` fires on that transition too;
  // this flag lets the close handler skip the destructive state-clear so "Back" returns to
  // a populated dialog. Only a genuine dismissal (X / backdrop / Cancel) clears the state.
  const editDialogTransitionRef = useRef(false);
  // Same idea for the secondary Prism dialogs (Migrate / Add Projects / Review / Rename /
  // Final Delete). Any Back / Review / Confirm button hides the current dialog, which fires
  // its `prismClose`; this flag lets the shared close handler skip the dismiss cleanup on a
  // controlled transition. Only a genuine dismissal (X / backdrop / Escape) runs cleanup.
  const subDialogTransitionRef = useRef(false);

  // Wraps a sub-dialog's dismiss cleanup so `prismClose` only runs it on a real dismissal,
  // not on a controlled Back/Review/Confirm transition (which set subDialogTransitionRef).
  const guardedSubDialogClose = (dismiss: () => void) => () => {
    if (subDialogTransitionRef.current) {
      subDialogTransitionRef.current = false;
      return;
    }
    dismiss();
  };

  // Cancel-button handler: mark the close as controlled (so guardedSubDialogClose skips the
  // duplicate cleanup that its own prismClose would fire) then run the dismiss cleanup.
  const dismissSubDialog = (dismiss: () => void) => () => {
    subDialogTransitionRef.current = true;
    dismiss();
  };

  useEffect(() => {
    isMountedRef.current = true;
    fetchTags();
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchTags = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/tags');
      setTags(response.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const sortedTags = [...tags]
    .filter(tag => tag.name.toLowerCase().includes(filterText.toLowerCase()))
    .sort((a, b) => {
      const comparison = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      return sortAsc ? comparison : -comparison;
    });

  const handleAdd = async () => {
    const name = newTag.trim();
    const definition = newTagDescription.trim();
    if (!name) return;
    if (!definition) {
      toast.error('A definition is required for every tag — this prevents tag drift.');
      return;
    }
    if (tags.some((tag) => tag.name === name)) {
      toast.error(`Tag "${name}" already exists.`);
      return;
    }
    try {
      const maxId = tags.length > 0 ? Math.max(...tags.map(t => t.id)) : 0;
      const response = await axios.post('/api/tags', {
        id: maxId + 1,
        name,
        description: definition,
      });

      setTags(prevTags => [...prevTags, response.data]);
      setNewTag('');
      setNewTagDescription('');
      toast.success(`Tag "${response.data.name}" added successfully!`);
    } catch (error) {
      console.error('Error adding tag:', error);
      const errorMsg = (error as any).response?.data?.message || (error as any).message || 'Failed to add tag';
      toast.error(errorMsg);
    }
  };

  // Save the definition, then the name (name changes on an in-use tag open a confirm modal).
  const handleSaveEdits = async () => {
    if (!tagToDelete) return;
    const tagId = tagToDelete.id;
    const descOk = await handleSaveDescriptionChange(tagId);
    if (!descOk) return; // blank/failed definition — keep the dialog open to fix it
    const nameResult = await handleSaveNameChange(tagId);
    // 'confirm' keeps this dialog open behind the rename-confirm modal (which closes both);
    // 'error' (duplicate) keeps it open so the name can be corrected.
    if (nameResult === 'confirm' || nameResult === 'error') return;
    closeEditDialog();
  };

  const closeEditDialog = () => {
    setShowDeleteModal(false);
    setTagToDelete(null);
    setEditingName('');
    setEditingDescription('');
  };

  type NameSaveResult = 'renamed' | 'confirm' | 'noop' | 'error';

  const handleSaveNameChange = async (tagId: number): Promise<NameSaveResult> => {
    try {
      const tag = tags.find(t => t.id === tagId);
      const newName = editName.trim();
      if (!tag || !newName || newName === tag.name) return 'noop';

      // Check for duplicate name
      if (tags.some(t => t.id !== tagId && t.name.toLowerCase() === newName.toLowerCase())) {
        toast.error('A tag with this name already exists');
        return 'error';
      }

      // Check if tag is used in projects - if so, show confirmation
      const countResponse = await axios.get(`/api/tags/${tagId}/count`);
      const projectCount = countResponse.data.count;

      if (projectCount > 0) {
        // Hide the edit dialog and show the rename confirmation (handleConfirmRename finishes
        // the rename; Back returns to the edit dialog). Guard both dialogs' prismClose.
        setTagToRename({ id: tagId, oldName: tag.name, newName });
        setTagToRenameProjectCount(projectCount);
        editDialogTransitionRef.current = true;
        setShowDeleteModal(false);
        setShowRenameConfirmModal(true);
        return 'confirm';
      }

      // No projects using this tag - rename directly
      await performRename(tagId, newName);
      return 'renamed';
    } catch (error) {
      console.error('Error updating tag name:', error);
      toast.error('Failed to update tag name');
      return 'error';
    }
  };
  
  const performRename = async (tagId: number, newName: string, projectCount = 0) => {
    try {
      await axios.put('/api/tags/name', {
        id: tagId,
        name: newName,
      });
      
      setTags(prevTags =>
        prevTags.map(t => t.id === tagId ? { ...t, name: newName } : t)
      );
      const projectText = projectCount === 1 ? '1 project' : `${projectCount} projects`;
      const message = projectCount > 0 
        ? `Tag renamed to "${newName}" successfully! (${projectText} updated)` 
        : `Tag renamed to "${newName}" successfully!`;
      toast.success(message);
    } catch (error) {
      console.error('Error updating tag name:', error);
      const errorMsg = (error as any).response?.data?.message || (error as any).message || 'Failed to update tag name';
      toast.error(errorMsg);
    }
  };
  
  const handleSaveDescriptionChange = async (tagId: number): Promise<boolean> => {
    try {
      const tag = tags.find(t => t.id === tagId);
      if (!tag) return true;

      const newDescription = editDescription.trim();
      if (!newDescription) {
        toast.error(`A definition is required for "${tag.name}" and cannot be blank.`);
        return false;
      }
      // No change (compared trimmed) — valid, nothing to save.
      if (newDescription === (tag.description || '').trim()) return true;

      await axios.put('/api/tags/description', {
        id: tagId,
        description: newDescription,
      });

      setTags(prevTags =>
        prevTags.map(t => (t.id === tagId ? { ...t, description: newDescription } : t))
      );
      toast.success(`Definition for "${tag.name}" updated successfully!`);
      return true;
    } catch (error) {
      console.error('Error updating tag description:', error);
      const errorMsg = (error as any).response?.data?.message || (error as any).message || 'Failed to update tag definition';
      toast.error(errorMsg);
      return false;
    }
  };

  const handleConfirmRename = async () => {
    if (!tagToRename) return;
    
    try {
      await performRename(tagToRename.id, tagToRename.newName, tagToRenameProjectCount);
      subDialogTransitionRef.current = true;
      setShowRenameConfirmModal(false);
      setTagToRename(null);
      // Rename came from the edit dialog (already hidden) — fully close it.
      closeEditDialog();
    } catch (error) {
      console.error('Error renaming tag:', error);
    }
  };

  // Rename "Back": return to the edit dialog, keeping the pending new name in the field.
  const handleBackToEditFromRename = () => {
    subDialogTransitionRef.current = true;
    setShowRenameConfirmModal(false);
    setShowDeleteModal(true);
  };

  // Rename "Cancel"/dismiss: abort the rename, revert the name field, return to the edit dialog.
  const handleCancelRename = () => {
    const originalTag = tags.find(t => t.id === tagToRename?.id);
    if (originalTag) {
      setEditingName(originalTag.name);
    }
    setShowRenameConfirmModal(false);
    setShowDeleteModal(true);
    setTagToRename(null);
  };

  const openEditDialog = async (tagId: number) => {
    const tag = tags.find(t => t.id === tagId);
    if (!tag) return;

    setTagToDelete(tag);
    setEditingName(tag.name);
    setEditingDescription(tag.description || '');
    setDeleteConfirmText('');

    try {
      const response = await axios.get(`/api/tags/${tagId}/count`);
      setTagToDeleteProjectCount(response.data.projectCount);
    } catch (error) {
      console.error('Error fetching tag usage count:', error);
      setTagToDeleteProjectCount(0);
    }

    setShowDeleteModal(true);
  };
  // Keep the ref current so the imperative PrismTable action cell calls the latest closure.
  openEditRef.current = openEditDialog;

  const handleCancelDelete = () => {
    setShowAddProjectsModal(false);
    setShowAddProjectsReviewModal(false);
    setSelectedProjectsToAdd([]);
    setAvailableProjects([]);
    setProjectSearchFilter('');
    setShowAddProjectsAffectedProjects(false);
    closeEditDialog();
  };

  // Wired to the edit dialog's onPrismClose. Skips the destructive clear when we're only
  // hiding the dialog to show a sub-modal (Migrate / Add Projects / Final Delete), so the
  // tag's name + definition survive a Back. A real dismissal (X / backdrop) still cancels.
  const handleEditDialogClose = () => {
    if (editDialogTransitionRef.current) {
      editDialogTransitionRef.current = false;
      return;
    }
    handleCancelDelete();
  };

  const handleCancelFinalDelete = () => {
    setShowFinalDeleteConfirmModal(false);
    setDeleteConfirmText('');
    setFinalDeleteModalError('');
    setShowFinalDeleteAffectedProjects(false);
    setFinalDeleteAffectedProjects([]);
    closeEditDialog();
  };

  const handleBackToModifyFromFinalDelete = () => {
    subDialogTransitionRef.current = true;
    setShowFinalDeleteConfirmModal(false);
    setDeleteConfirmText('');
    setFinalDeleteModalError('');
    setShowFinalDeleteAffectedProjects(false);
    setFinalDeleteAffectedProjects([]);
    setShowDeleteModal(true);
  };

  const handleShowAddProjects = async () => {
    if (!tagToDelete) return;
    setTagToAddProjects(tagToDelete);
    editDialogTransitionRef.current = true;
    setShowDeleteModal(false);
    setShowAddProjectsModal(true);
    setSelectedProjectsToAdd([]);
    
    // Fetch available projects (projects without this tag)
    try {
      setLoadingAvailableProjects(true);
      const response = await axios.get(`/api/tags/${tagToDelete.id}/available-projects`);
      setAvailableProjects(response.data.projects || []);
    } catch (error) {
      console.error('Error fetching available projects:', error);
      toast.error('Failed to load available projects');
    } finally {
      setLoadingAvailableProjects(false);
    }
  };

  const handleBackToModifyFromAddProjects = () => {
    subDialogTransitionRef.current = true;
    setShowAddProjectsModal(false);
    setSelectedProjectsToAdd([]);
    setAvailableProjects([]);
    setProjectSearchFilter('');
    setShowAddProjectsReviewModal(false);
    setShowAddProjectsAffectedProjects(false);
    setShowDeleteModal(true);
  };

  const handleShowAddProjectsReview = () => {
    subDialogTransitionRef.current = true;
    setShowAddProjectsModal(false);
    setShowAddProjectsReviewModal(true);
    setShowAddProjectsAffectedProjects(false);
  };

  const handleBackToAddProjectsFromReview = () => {
    subDialogTransitionRef.current = true;
    setShowAddProjectsReviewModal(false);
    setShowAddProjectsAffectedProjects(false);
    setShowAddProjectsModal(true);
  };

  const handleToggleAddProjectsAffectedProjects = () => {
    setShowAddProjectsAffectedProjects(!showAddProjectsAffectedProjects);
  };

  const handleToggleProjectSelection = (projectId: number) => {
    setSelectedProjectsToAdd(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const handleConfirmAddProjects = async () => {
    if (!tagToAddProjects || selectedProjectsToAdd.length === 0) return;
    
    try {
      setLoading(true);
      await axios.post('/api/tags/add-projects', {
        tagId: tagToAddProjects.id,
        projectIds: selectedProjectsToAdd
      });
      
      subDialogTransitionRef.current = true;
      setShowAddProjectsModal(false);
      setShowAddProjectsReviewModal(false);
      setSelectedProjectsToAdd([]);
      setAvailableProjects([]);
      setProjectSearchFilter('');
      setShowAddProjectsAffectedProjects(false);
      setTagToAddProjects(null);
      closeEditDialog();
      toast.success(`Successfully added ${selectedProjectsToAdd.length} project(s) to tag "${tagToAddProjects.name}"`);
    } catch (error) {
      console.error('Error adding projects to tag:', error);
      const errorMsg = (error as any).response?.data?.message || (error as any).message || 'Failed to add projects to tag';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFinalDeleteAffectedProjects = async () => {
    if (!showFinalDeleteAffectedProjects && finalDeleteAffectedProjects.length === 0 && tagToDelete) {
      // Fetch projects if not already loaded
      try {
        const response = await axios.get(`/api/tags/${tagToDelete.id}/projects`);
        setFinalDeleteAffectedProjects(response.data.projects || []);
      } catch (error) {
        console.error('Error fetching affected projects:', error);
        toast.error('Failed to load affected projects');
      }
    }
    setShowFinalDeleteAffectedProjects(!showFinalDeleteAffectedProjects);
  };

  const handleToggleRenameAffectedProjects = async () => {
    if (!showRenameAffectedProjects && renameAffectedProjects.length === 0 && tagToRename) {
      // Fetch projects if not already loaded
      try {
        const response = await axios.get(`/api/tags/${tagToRename.id}/projects`);
        setRenameAffectedProjects(response.data.projects || []);
      } catch (error) {
        console.error('Error fetching affected projects:', error);
        toast.error('Failed to load affected projects');
      }
    }
    setShowRenameAffectedProjects(!showRenameAffectedProjects);
  };

  const handleCloseMigrateModal = () => {
    setShowMigrateModal(false);
    setShowMigrateReviewModal(false);
    setShowAddProjectsModal(false);
    setShowAddProjectsReviewModal(false);
    setSelectedProjectsToAdd([]);
    setAvailableProjects([]);
    setProjectSearchFilter('');
    setShowAddProjectsAffectedProjects(false);
    closeEditDialog();
  };

  const handleBackToDeleteModal = () => {
    subDialogTransitionRef.current = true;
    setShowMigrateModal(false);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!tagToDelete) return;
    
    // Always show final confirmation modal
    editDialogTransitionRef.current = true;
    setShowDeleteModal(false);
    setDeleteConfirmText('');
    setShowFinalDeleteConfirmModal(true);
  };
  
  const handleFinalDeleteConfirm = async () => {
    if (deleteConfirmText !== 'I CLEARly want to delete this tag') {
      setFinalDeleteModalError('Please type the confirmation text exactly as shown');
      return;
    }
    
    await performDelete();
    subDialogTransitionRef.current = true;
    setShowFinalDeleteConfirmModal(false);
    setDeleteConfirmText('');
    setFinalDeleteModalError('');
  };
  
  const performDelete = async () => {
    if (!tagToDelete) return;
    
    try {
      setLoading(true);
      await axios.delete(`/api/tags/${tagToDelete.id}`);
      const deletedTagName = tagToDelete.name;
      setTags(tags.filter(t => t.id !== tagToDelete.id));
      setShowDeleteModal(false);
      setTagToDelete(null);
      setEditingName('');
      setEditingDescription('');
      const projectText = tagToDeleteProjectCount === 1 ? '1 project' : `${tagToDeleteProjectCount} projects`;
      const message = tagToDeleteProjectCount > 0
        ? `Tag "${deletedTagName}" deleted successfully! (${projectText} no longer tagged)`
        : `Tag "${deletedTagName}" deleted successfully!`;
      toast.success(message);
    } catch (error) {
      console.error('Error deleting tag:', error);
      const errorMsg = (error as any).response?.data?.message || (error as any).message || 'Failed to delete tag';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleShowMigrateModal = () => {
    editDialogTransitionRef.current = true;
    setShowDeleteModal(false);
    setShowMigrateModal(true);
    setSelectedMigrateToTag(null);
    setCreateNewMigrateTag(false);
    setNewMigrateTagName('');
  };

  const handleShowMigrateReview = async () => {
    // Validate new tag name doesn't already exist
    if (createNewMigrateTag && newMigrateTagName.trim()) {
      const existingTag = tags.find(
        t => t.name.toLowerCase() === newMigrateTagName.trim().toLowerCase()
      );
      if (existingTag) {
        toast.error(`A tag with the name "${newMigrateTagName.trim()}" already exists. Please choose a different name.`);
        return;
      }
    }
    
    subDialogTransitionRef.current = true;
    setShowMigrateModal(false);
    setShowMigrateReviewModal(true);
    setShowAffectedProjects(false);
    setAffectedProjects([]);
  };

  const handleToggleAffectedProjects = async () => {
    if (!showAffectedProjects && affectedProjects.length === 0 && tagToDelete) {
      // Fetch projects if not already loaded
      try {
        const response = await axios.get(`/api/tags/${tagToDelete.id}/projects`);
        setAffectedProjects(response.data.projects || []);
      } catch (error) {
        console.error('Error fetching affected projects:', error);
        toast.error('Failed to load affected projects');
      }
    }
    setShowAffectedProjects(!showAffectedProjects);
  };

  const handleBackToMigrateModal = () => {
    subDialogTransitionRef.current = true;
    setShowMigrateReviewModal(false);
    setShowMigrateModal(true);
  };

  const handleMigrateTag = async () => {
    if (!tagToDelete) return;
    
    let targetTagId = selectedMigrateToTag;
    let targetTagName = '';
    
    // If creating a new tag for migration
    if (createNewMigrateTag && newMigrateTagName.trim()) {
      try {
        const maxId = tags.length > 0 ? Math.max(...tags.map(t => t.id)) : 0;
        const response = await axios.post('/api/tags', {
          id: maxId + 1,
          name: newMigrateTagName.trim(),
        });
        targetTagId = response.data.id;
        targetTagName = response.data.name;
        setTags(prevTags => [...prevTags, response.data]);
      } catch (error) {
        console.error('Error creating new tag:', error);
        const errorMsg = (error as any).response?.data?.error || (error as any).message || 'Failed to create new tag';
        toast.error(errorMsg);
        return;
      }
    } else {
      const targetTag = tags.find(t => t.id === targetTagId);
      targetTagName = targetTag?.name || '';
    }
    
    if (!targetTagId) {
      toast.error('Please select or create a tag to migrate to');
      return;
    }
    
    try {
      setLoading(true);
      await axios.post('/api/tags/migrate', {
        fromTagId: tagToDelete.id,
        toTagId: targetTagId,
      });
      
      const deletedTagName = tagToDelete.name;
      const migratedProjectCount = tagToDeleteProjectCount;
      setTags(tags.filter(t => t.id !== tagToDelete.id));
      subDialogTransitionRef.current = true;
      setShowMigrateModal(false);
      setShowMigrateReviewModal(false);
      setTagToDelete(null);
      setEditingName('');
      setEditingDescription('');
      const projectText = migratedProjectCount === 1 ? '1 project' : `${migratedProjectCount} projects`;
      const message = migratedProjectCount > 0
        ? `Tag "${deletedTagName}" migrated to "${targetTagName}" and deleted successfully! (${projectText} migrated)`
        : `Tag "${deletedTagName}" migrated to "${targetTagName}" and deleted successfully!`;
      toast.success(message);
    } catch (error) {
      console.error('Error migrating tag:', error);
      const errorMsg = (error as any).response?.data?.message || (error as any).message || 'Failed to migrate tag';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const toggleSort = () => {
    setSortAsc(!sortAsc);
  };

  // PrismTable rows + column definitions. Name and Definition are plain text columns;
  // the Actions column renders an imperative prism-button node (TanStack cells return DOM
  // nodes, not React), wired to open the edit dialog via the always-current openEditRef.
  const tableRows = sortedTags.map((tag) => ({
    id: tag.id,
    name: tag.name,
    definition: tag.description || '',
  }));

  // prism-table builds its TanStack instance from `data` and (re-tested on 9.2.0) does not
  // re-render a row whose id stays the same but whose content changes, so a renamed tag
  // wouldn't appear without a manual refresh. Remount the table whenever
  // the rendered row set changes — the table holds no internal state we need to preserve
  // (sort + filter are external), and the list is small, so a remount is cheap and correct.
  const tableKey = tableRows
    .map((r) => `${r.id}:${r.name}:${r.definition}`)
    .join('|');

  const tableColumns = useMemo(
    () => [
      { accessorKey: 'name', header: 'Tag Name', size: 200, minSize: 140 },
      {
        accessorKey: 'definition',
        header: 'Definition',
        size: 560,
        minSize: 260,
        cell: (info: any) => (info.getValue() as string) || '—',
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        size: 116,
        minSize: 104,
        cell: (info: any) => {
          const btn = document.createElement('prism-button');
          btn.setAttribute('size', 'sm');
          btn.setAttribute('tone', 'primary');
          btn.setAttribute('display', 'outline');
          // Fill the cell so the button never overflows the fixed-width, overflow-hidden
          // Actions cell (which would clip it) and leaves no dead space beside it.
          btn.style.width = '100%';
          btn.textContent = 'Modify';
          btn.addEventListener('prismClick', () => {
            openEditRef.current(Number(info.row.original.id));
          });
          return btn;
        },
      },
    ],
    [],
  );

  return (
    <AdminPane as="div" width="full" className="admin tags-page-container">
      <div className="tags-header">
        <PrismBanner tone="info" layout="inline" className="clr:mb-4">
          <strong>Project Tags Management:</strong>
          <ul className="clr:mb-0 clr:mt-2" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
            <li>Tags help categorize projects</li>
            <li>Use the <strong>Modify</strong> button on a row to rename a tag, edit its definition, add projects, migrate projects to another tag, or delete a tag</li>
            <li>Every tag requires a definition — this keeps the taxonomy consistent and prevents drift</li>
          </ul>
        </PrismBanner>

        <div className="tags-add-form">
          <label className="tags-add-label" htmlFor="newTagInput">Add New Tag</label>
          <div className="tags-add-row">
            <PrismInputText
              name="newTag"
              label="Add New Tag"
              hideLabel
              value={newTag}
              placeholder="Enter tag name"
              onPrismInput={(e: any) => setNewTag(e.detail?.value ?? e.target?.value ?? '')}
              onPrismChange={(e: any) => setNewTag(e.detail?.value ?? e.target?.value ?? '')}
              onPrismKeyup={(e: any) => {
                const key = e.detail?.key ?? e.detail?.code ?? e.key;
                if (key === 'Enter') handleAdd();
              }}
            />
            <PrismButton
              tone="primary"
              onPrismClick={handleAdd}
              disabled={!newTag.trim() || !newTagDescription.trim()}
            >
              Add Tag
            </PrismButton>
          </div>
          <PrismInputTextarea
            name="newTagDescription"
            label="New tag definition"
            hideLabel
            required
            rows={2}
            value={newTagDescription}
            placeholder="Required — define what this tag means and what qualifies for it"
            onPrismInput={(e: any) => setNewTagDescription((e.detail?.value ?? e.target?.value ?? '').slice(0, 500))}
            onPrismChange={(e: any) => setNewTagDescription((e.detail?.value ?? e.target?.value ?? '').slice(0, 500))}
          />
          <div className="tags-add-footer">
            <p className="tags-add-hint">
              A definition is required for every tag. It keeps the taxonomy consistent and prevents tag drift.
            </p>
            <span className="tags-add-counter">{newTagDescription.length}/500</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="clr:text-center" style={{ padding: '2rem' }}>
          <PrismLoading as="spinner" container="inline" />
        </div>
      ) : (
        <div className="tags-table-wrapper">
          <div className="tags-toolbar">
            <div className="tags-filter">
              <PrismInputText
                name="tagFilter"
                label="Filter tags"
                hideLabel
                value={filterText}
                placeholder="Filter tags..."
                onPrismInput={(e: any) => setFilterText(e.detail?.value ?? e.target?.value ?? '')}
                onPrismChange={(e: any) => setFilterText(e.detail?.value ?? e.target?.value ?? '')}
              />
              {filterText && (
                <PrismButton
                  tone="secondary"
                  display="outline"
                  size="sm"
                  onPrismClick={() => setFilterText('')}
                  title="Clear filter"
                  className="tags-filter-clear"
                >
                  ×
                </PrismButton>
              )}
            </div>
            <PrismButton
              tone="secondary"
              display="outline"
              onPrismClick={toggleSort}
              title="Toggle sort order"
              className="tags-sort-btn"
            >
              Sort: {sortAsc ? 'A → Z' : 'Z → A'}
            </PrismButton>
          </div>

          <PrismTable key={tableKey} data={tableRows} columns={tableColumns} />

          {tableRows.length === 0 && (
            <div className="tags-empty clr:text-secondary-base" role="status">
              {filterText ? 'No tags match your filter.' : 'No tags yet — add one above.'}
            </div>
          )}
        </div>
      )}

      {/* Edit Tag dialog (Prism) */}
      <PrismDialog
        open={showDeleteModal}
        setAriaLabel={tagToDelete ? `Edit tag ${tagToDelete.name}` : 'Edit tag'}
        onPrismClose={handleEditDialogClose}
      >
        <span slot="header">Edit Tag: "{tagToDelete?.name}"</span>

        <div className="tags-edit-fields">
          <PrismInputText
            name="editTagName"
            label="Tag name"
            required
            value={editName}
            onPrismInput={(e: any) => setEditingName(e.detail?.value ?? e.target?.value ?? '')}
            onPrismChange={(e: any) => setEditingName(e.detail?.value ?? e.target?.value ?? '')}
          />
          <PrismInputTextarea
            name="editTagDefinition"
            label="Definition"
            required
            rows={3}
            maxlength={500}
            value={editDescription}
            placeholder="Definition required — what this tag means and what qualifies for it"
            onPrismInput={(e: any) => setEditingDescription(e.detail?.value ?? e.target?.value ?? '')}
            onPrismChange={(e: any) => setEditingDescription(e.detail?.value ?? e.target?.value ?? '')}
          />
          <p className="tags-edit-hint">
            This tag is currently used by <strong>{tagToDeleteProjectCount}</strong>{' '}
            {tagToDeleteProjectCount === 1 ? 'project' : 'projects'}. Renaming it updates every
            associated project. A definition is required.
          </p>
        </div>

        <div slot="footer" className="tags-edit-footer">
          <PrismButton tone="primary" onPrismClick={handleSaveEdits}>
            Save changes
          </PrismButton>
          <PrismButton tone="primary" display="outline" onPrismClick={handleShowAddProjects}>
            Add Projects
          </PrismButton>
          {tagToDeleteProjectCount > 0 && (
            <PrismButton tone="info" display="outline" onPrismClick={handleShowMigrateModal}>
              Migrate
            </PrismButton>
          )}
          <PrismButton tone="danger" display="outline" onPrismClick={handleConfirmDelete}>
            Delete Tag
          </PrismButton>
          <PrismButton tone="secondary" display="outline" onPrismClick={handleCancelDelete}>
            Cancel
          </PrismButton>
        </div>
      </PrismDialog>

      {/* Migrate dialog (Prism) */}
      <PrismDialog
        open={showMigrateModal}
        setAriaLabel={tagToDelete ? `Migrate tag ${tagToDelete.name}` : 'Migrate tag'}
        onPrismClose={guardedSubDialogClose(handleCloseMigrateModal)}
      >
        <span slot="header">Migrate Tag: "{tagToDelete?.name}"</span>
        <div className="tags-dialog-body tags-dialog-body--lg">
          <p>
            All {tagToDeleteProjectCount} project(s) currently using "{tagToDelete?.name}"
            will be updated to use the selected tag instead.
          </p>

          <PrismSelect
            label="Select existing tag to migrate to"
            name="migrateTo"
            value={selectedMigrateToTag != null ? String(selectedMigrateToTag) : ''}
            disabled={createNewMigrateTag}
            options={[
              { value: '', display: '-- Select a tag --' },
              ...tags
                .filter(t => t.id !== tagToDelete?.id)
                .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                .map(t => ({ value: String(t.id), display: t.name })),
            ]}
            onPrismChange={(e: any) => {
              const val = e.detail?.value ?? e.detail ?? '';
              setSelectedMigrateToTag(val ? parseInt(String(val), 10) : null);
              setCreateNewMigrateTag(false);
            }}
          />

          <div className="tags-dialog-or">-- OR --</div>

          <PrismCheckbox
            name="createNewMigrateTag"
            label="Create a new tag to migrate to"
            checked={createNewMigrateTag}
            onPrismChange={onCheckedChange(createNewMigrateTag, (next) => {
              setCreateNewMigrateTag(next);
              if (next) setSelectedMigrateToTag(null);
            })}
          />

          {createNewMigrateTag && (
            <div>
              <PrismInputText
                name="newMigrateTagName"
                label="New tag name"
                value={newMigrateTagName}
                placeholder="Enter new tag name"
                onPrismInput={(e: any) => setNewMigrateTagName(e.detail?.value ?? e.target?.value ?? '')}
                onPrismChange={(e: any) => setNewMigrateTagName(e.detail?.value ?? e.target?.value ?? '')}
              />
              {newMigrateTagName.trim() !== '' &&
                tags.some(t => t.name.toLowerCase() === newMigrateTagName.trim().toLowerCase()) && (
                  <p className="tags-dialog-error">
                    A tag with this name already exists. Please choose a different name.
                  </p>
                )}
            </div>
          )}
        </div>
        <div slot="footer" className="tags-edit-footer">
          <PrismButton tone="info" display="outline" onPrismClick={handleBackToDeleteModal}>
            Back
          </PrismButton>
          <PrismButton
            tone="primary"
            onPrismClick={handleShowMigrateReview}
            disabled={
              !selectedMigrateToTag &&
              (!createNewMigrateTag || !newMigrateTagName.trim() ||
                tags.some(t => t.name.toLowerCase() === newMigrateTagName.trim().toLowerCase()))
            }
          >
            Review
          </PrismButton>
          <PrismButton tone="secondary" display="outline" onPrismClick={dismissSubDialog(handleCloseMigrateModal)}>
            Cancel
          </PrismButton>
        </div>
      </PrismDialog>

      {/* Add Projects dialog (Prism) */}
      <PrismDialog
        open={showAddProjectsModal}
        setAriaLabel={tagToAddProjects ? `Add projects to tag ${tagToAddProjects.name}` : 'Add projects to tag'}
        onPrismClose={guardedSubDialogClose(handleCloseMigrateModal)}
      >
        <span slot="header">Add Projects to Tag: "{tagToAddProjects?.name}"</span>
        <div className="tags-dialog-body tags-dialog-body--lg tags-dialog-body--tall">
          {loadingAvailableProjects ? (
            <div className="clr:text-center clr:py-6">
              <PrismLoading as="spinner" container="inline" />
              <p className="clr:mt-2">Loading available projects...</p>
            </div>
          ) : availableProjects.length === 0 ? (
            <PrismBanner tone="info" layout="inline">
              All projects already have this tag assigned.
            </PrismBanner>
          ) : (
            <>
              <div className="tags-dialog-addprojects-toolbar">
                <div>
                  Select projects to add to this tag ({selectedProjectsToAdd.length} selected):
                </div>
                <div className="tags-dialog-search">
                  <PrismInputText
                    name="projectSearchFilter"
                    label="Search projects"
                    hideLabel
                    value={projectSearchFilter}
                    placeholder="Search projects..."
                    onPrismInput={(e: any) => setProjectSearchFilter(e.detail?.value ?? e.target?.value ?? '')}
                    onPrismChange={(e: any) => setProjectSearchFilter(e.detail?.value ?? e.target?.value ?? '')}
                  />
                  {projectSearchFilter && (
                    <PrismButton
                      tone="secondary"
                      display="outline"
                      onPrismClick={() => setProjectSearchFilter('')}
                    >
                      ×
                    </PrismButton>
                  )}
                </div>
              </div>

              <div className="tags-dialog-project-list">
                {availableProjects
                  .filter(project =>
                    project.name.toLowerCase().includes(projectSearchFilter.toLowerCase())
                  )
                  .map(project => (
                    <PrismCheckbox
                      key={project.id}
                      name={`project-${project.id}`}
                      label={project.name}
                      checked={selectedProjectsToAdd.includes(project.id)}
                      onPrismChange={onCheckedChange(
                        selectedProjectsToAdd.includes(project.id),
                        () => handleToggleProjectSelection(project.id)
                      )}
                    />
                  ))}
                {availableProjects.filter(project =>
                  project.name.toLowerCase().includes(projectSearchFilter.toLowerCase())
                ).length === 0 && projectSearchFilter && (
                  <div className="clr:text-secondary-base clr:text-center clr:py-4">
                    No projects match your search.
                  </div>
                )}
              </div>

              <div style={{ minHeight: '48px' }}>
                {selectedProjectsToAdd.length > 0 && (
                  <PrismBanner tone="success" layout="inline" className="clr:mb-0">
                    {selectedProjectsToAdd.length} project(s) will be tagged with "{tagToAddProjects?.name}"
                  </PrismBanner>
                )}
              </div>
            </>
          )}
        </div>
        <div slot="footer" className="tags-edit-footer">
          <PrismButton tone="info" display="outline" onPrismClick={handleBackToModifyFromAddProjects}>
            Back
          </PrismButton>
          <PrismButton
            tone="primary"
            onPrismClick={handleShowAddProjectsReview}
            disabled={selectedProjectsToAdd.length === 0}
          >
            Review
          </PrismButton>
          <PrismButton tone="secondary" display="outline" onPrismClick={dismissSubDialog(handleCloseMigrateModal)}>
            Cancel
          </PrismButton>
        </div>
      </PrismDialog>

      {/* Add Projects Review dialog (Prism) */}
      <PrismDialog
        open={showAddProjectsReviewModal}
        setAriaLabel="Review add projects"
        onPrismClose={guardedSubDialogClose(handleCloseMigrateModal)}
      >
        <span slot="header">Review Add Projects</span>
        <div className="tags-dialog-body">
          <p className="clr:mb-4">Please review the details before adding:</p>

          <div className="tags-dialog-review-box clr:p-4 clr:mb-4">
            <ul className="tags-list-plain clr:mb-0">
              <li className="clr:mb-2">
                <strong>Tag:</strong>
                <ul className="tags-list-plain clr:ml-4 clr:mt-1">
                  <li className="clr:text-success-base">"{tagToAddProjects?.name}"</li>
                </ul>
              </li>
              <li>
                <strong>Projects to add:</strong>
                <ul className="tags-list-plain clr:ml-4 clr:mt-1">
                  <li>
                    {selectedProjectsToAdd.length} project(s)
                    {selectedProjectsToAdd.length > 0 && (
                      <PrismButton
                        display="link"
                        tone="info"
                        size="sm"
                        spacing="compact"
                        setAriaExpanded={showAddProjectsAffectedProjects}
                        onPrismClick={handleToggleAddProjectsAffectedProjects}
                        className="tags-disclosure-btn"
                      >
                        {showAddProjectsAffectedProjects ? 'Hide' : 'Show'} projects
                      </PrismButton>
                    )}
                  </li>
                </ul>
                {selectedProjectsToAdd.length > 0 && showAddProjectsAffectedProjects && (
                  <div id="add-projects-affected-collapse" className="clr:ml-4 clr:mt-2">
                    <div className="tags-dialog-scroll-list clr:p-2">
                      <ul className="clr:mb-0" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
                        {availableProjects
                          .filter(p => selectedProjectsToAdd.includes(p.id))
                          .map(project => (
                            <li key={project.id}>{project.name}</li>
                          ))}
                      </ul>
                    </div>
                  </div>
                )}
              </li>
            </ul>
          </div>

          <PrismBanner tone="info" layout="inline">
            <strong>This action will:</strong>
            <ul className="clr:mb-0 clr:mt-2" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
              <li>Add the tag "{tagToAddProjects?.name}" to {selectedProjectsToAdd.length} project(s)</li>
            </ul>
          </PrismBanner>
        </div>
        <div slot="footer" className="tags-edit-footer">
          <PrismButton tone="info" display="outline" onPrismClick={handleBackToAddProjectsFromReview}>
            Back
          </PrismButton>
          <PrismButton tone="primary" onPrismClick={handleConfirmAddProjects}>
            Confirm Add {selectedProjectsToAdd.length} Project{selectedProjectsToAdd.length !== 1 ? 's' : ''}
          </PrismButton>
          <PrismButton tone="secondary" display="outline" onPrismClick={dismissSubDialog(handleCloseMigrateModal)}>
            Cancel
          </PrismButton>
        </div>
      </PrismDialog>

      {/* Migrate Review dialog (Prism) */}
      <PrismDialog
        open={showMigrateReviewModal}
        setAriaLabel="Review migration"
        onPrismClose={guardedSubDialogClose(handleCloseMigrateModal)}
      >
        <span slot="header">Review Migration</span>
        <div className="tags-dialog-body">
          <p className="clr:mb-4">Please review the migration details before confirming:</p>

          <div className="tags-dialog-review-box clr:p-4 clr:mb-4">
            <ul className="tags-list-plain clr:mb-0">
              <li className="clr:mb-2">
                <strong>From:</strong> <small className="clr:text-secondary-base">(will be deleted)</small>
                <ul className="tags-list-plain clr:ml-4 clr:mt-1">
                  <li className="clr:text-danger-base">"{tagToDelete?.name}"</li>
                </ul>
              </li>
              <li className="clr:mb-2">
                <strong>To:</strong> <small className="clr:text-secondary-base">(destination)</small>
                <ul className="tags-list-plain clr:ml-4 clr:mt-1">
                  <li className="clr:text-success-base">
                    {createNewMigrateTag ? (
                      <span>"{newMigrateTagName}"</span>
                    ) : (
                      <span>"{tags.find(t => t.id === selectedMigrateToTag)?.name}"</span>
                    )}
                  </li>
                </ul>
              </li>
              <li>
                <strong>Projects affected:</strong>
                <ul className="tags-list-plain clr:ml-4 clr:mt-1">
                  <li>
                    {tagToDeleteProjectCount} project(s)
                    {tagToDeleteProjectCount > 0 && (
                      <PrismButton
                        display="link"
                        tone="info"
                        size="sm"
                        spacing="compact"
                        setAriaExpanded={showAffectedProjects}
                        onPrismClick={handleToggleAffectedProjects}
                        className="tags-disclosure-btn"
                      >
                        {showAffectedProjects ? 'Hide' : 'Show'} projects
                      </PrismButton>
                    )}
                  </li>
                </ul>
                {tagToDeleteProjectCount > 0 && showAffectedProjects && (
                  <div id="affected-projects-collapse" className="clr:ml-4 clr:mt-2">
                    <div className="tags-dialog-scroll-list clr:p-2">
                      {affectedProjects.length === 0 ? (
                        <div className="clr:text-center clr:text-secondary-base clr:py-2">
                          <PrismLoading as="spinner" size="sm" container="inline" />
                          <span className="clr:ml-2">Loading projects...</span>
                        </div>
                      ) : (
                        <ul className="clr:mb-0" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
                          {affectedProjects.map(project => (
                            <li key={project.id}>{project.name}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </li>
            </ul>
          </div>

          <PrismBanner tone="caution" layout="inline">
            <strong>This action will:</strong>
            <ul className="clr:mb-0 clr:mt-2" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
              <li>Update all {tagToDeleteProjectCount} project(s) to use the new tag</li>
              {createNewMigrateTag && <li>Create the tag "{newMigrateTagName}"</li>}
              <li>Permanently delete the tag "{tagToDelete?.name}"</li>
            </ul>
          </PrismBanner>
        </div>
        <div slot="footer" className="tags-edit-footer">
          <PrismButton tone="info" display="outline" onPrismClick={handleBackToMigrateModal}>
            Back
          </PrismButton>
          <PrismButton tone="danger" onPrismClick={handleMigrateTag}>
            Confirm Migration & Delete
          </PrismButton>
          <PrismButton tone="secondary" display="outline" onPrismClick={dismissSubDialog(handleCloseMigrateModal)}>
            Cancel
          </PrismButton>
        </div>
      </PrismDialog>

      {/* Rename Confirmation dialog (Prism) */}
      <PrismDialog
        open={showRenameConfirmModal}
        setAriaLabel="Confirm tag rename"
        onPrismClose={guardedSubDialogClose(handleCancelRename)}
      >
        <span slot="header">Confirm Tag Rename</span>
        <div className="tags-dialog-body">
          <p className="clr:mb-4">Please review the rename details before confirming:</p>

          <div className="tags-dialog-review-box clr:p-4 clr:mb-4">
            <ul className="tags-list-plain clr:mb-0">
              <li className="clr:mb-2">
                <strong>From:</strong> <small className="clr:text-secondary-base">(current name)</small>
                <ul className="tags-list-plain clr:ml-4 clr:mt-1">
                  <li className="clr:text-primary-base">"{tagToRename?.oldName}"</li>
                </ul>
              </li>
              <li className="clr:mb-2">
                <strong>To:</strong> <small className="clr:text-secondary-base">(new name)</small>
                <ul className="tags-list-plain clr:ml-4 clr:mt-1">
                  <li className="clr:text-success-base">"{tagToRename?.newName}"</li>
                </ul>
              </li>
              <li>
                <strong>Projects affected:</strong>
                <ul className="tags-list-plain clr:ml-4 clr:mt-1">
                  <li>
                    {tagToRenameProjectCount} project(s)
                    {tagToRenameProjectCount > 0 && (
                      <PrismButton
                        display="link"
                        tone="info"
                        size="sm"
                        spacing="compact"
                        setAriaExpanded={showRenameAffectedProjects}
                        onPrismClick={handleToggleRenameAffectedProjects}
                        className="tags-disclosure-btn"
                      >
                        {showRenameAffectedProjects ? 'Hide' : 'Show'} projects
                      </PrismButton>
                    )}
                  </li>
                </ul>
                {tagToRenameProjectCount > 0 && showRenameAffectedProjects && (
                  <div id="rename-affected-projects-collapse" className="clr:ml-4 clr:mt-2">
                    <div className="tags-dialog-scroll-list clr:p-2">
                      {renameAffectedProjects.length === 0 ? (
                        <div className="clr:text-center clr:text-secondary-base clr:py-2">
                          <PrismLoading as="spinner" size="sm" container="inline" />
                          <span className="clr:ml-2">Loading projects...</span>
                        </div>
                      ) : (
                        <ul className="clr:mb-0" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
                          {renameAffectedProjects.map(project => (
                            <li key={project.id}>{project.name}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </li>
            </ul>
          </div>

          <PrismBanner tone="info" layout="inline">
            <strong>This action will:</strong>
            <ul className="clr:mb-0 clr:mt-2" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
              <li>Update the tag name from "{tagToRename?.oldName}" to "{tagToRename?.newName}"</li>
              <li>Apply the new name across all {tagToRenameProjectCount} associated project(s)</li>
            </ul>
          </PrismBanner>
        </div>
        <div slot="footer" className="tags-edit-footer">
          <PrismButton tone="info" display="outline" onPrismClick={handleBackToEditFromRename}>
            Back
          </PrismButton>
          <PrismButton tone="primary" onPrismClick={handleConfirmRename}>
            Rename
          </PrismButton>
          <PrismButton tone="secondary" display="outline" onPrismClick={handleCancelRename}>
            Cancel
          </PrismButton>
        </div>
      </PrismDialog>

      {/* Final Delete Confirmation dialog (Prism) */}
      <PrismDialog
        open={showFinalDeleteConfirmModal}
        setAriaLabel="Final delete confirmation"
        onPrismClose={guardedSubDialogClose(handleCancelFinalDelete)}
      >
        <span slot="header">Final Confirmation Required</span>
        <div className="tags-dialog-body">
          <p>You are about to permanently delete the tag:</p>
          <p className="clr:text-center">"{tagToDelete?.name}"</p>

          <PrismBanner tone="danger" layout="inline">
            <small>
              This will affect <strong>{tagToDeleteProjectCount}</strong> project(s).
              This action cannot be undone.
            </small>
            {tagToDeleteProjectCount > 0 && (
              <div className="clr:mt-2">
                <PrismButton
                  display="link"
                  tone="primary-inverse"
                  size="sm"
                  spacing="compact"
                  setAriaExpanded={showFinalDeleteAffectedProjects}
                  onPrismClick={handleToggleFinalDeleteAffectedProjects}
                  className="tags-disclosure-btn tags-disclosure-btn--on-danger"
                >
                  {showFinalDeleteAffectedProjects ? 'Hide' : 'Show'} affected projects
                </PrismButton>
              </div>
            )}
          </PrismBanner>

          {tagToDeleteProjectCount > 0 && showFinalDeleteAffectedProjects && (
            <div id="final-delete-affected-projects-collapse" className="clr:mb-4">
              <div className="tags-dialog-scroll-list clr:p-2">
                {finalDeleteAffectedProjects.length === 0 ? (
                  <div className="clr:text-center clr:text-secondary-base clr:py-2">
                    <PrismLoading as="spinner" size="sm" container="inline" />
                    <span className="clr:ml-2">Loading projects...</span>
                  </div>
                ) : (
                  <ul className="clr:mb-0" style={{ paddingLeft: '1.5rem', listStyleType: 'disc' }}>
                    {finalDeleteAffectedProjects.map(project => (
                      <li key={project.id}>{project.name}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          <div>
            <PrismInputText
              name="deleteConfirmText"
              label={'Type "I CLEARly want to delete this tag" to confirm:'}
              value={deleteConfirmText}
              placeholder="I CLEARly want to delete this tag"
              onPrismInput={(e: any) => {
                setDeleteConfirmText(e.detail?.value ?? e.target?.value ?? '');
                setFinalDeleteModalError('');
              }}
              onPrismChange={(e: any) => {
                setDeleteConfirmText(e.detail?.value ?? e.target?.value ?? '');
                setFinalDeleteModalError('');
              }}
            />
            {finalDeleteModalError && (
              <PrismBanner tone="danger" layout="inline" className="clr:mt-2 clr:mb-0">
                {finalDeleteModalError}
              </PrismBanner>
            )}
          </div>
        </div>
        <div slot="footer" className="tags-edit-footer">
          <PrismButton tone="info" display="outline" onPrismClick={handleBackToModifyFromFinalDelete}>
            Back
          </PrismButton>
          <PrismButton tone="danger" onPrismClick={handleFinalDeleteConfirm}>
            Confirm Delete
          </PrismButton>
          <PrismButton tone="secondary" display="outline" onPrismClick={dismissSubDialog(handleCancelFinalDelete)}>
            Cancel
          </PrismButton>
        </div>
      </PrismDialog>
    </AdminPane>
  );
};

export default ProjectTags;
